import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";

/**
 * Notifications bell — polls the in-app notification feed for the unread count and
 * exposes a dropdown of the latest items with click-to-mark-read. Backed by
 * `GET /api/notifications` — the same feed that a future VAPID web-push subscription
 * will consume.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setUnread(data.unread || 0);
      setItems(data.items || []);
    } catch (err) {
      void err;
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // Poll every 30s. VAPID push will make this real-time later.
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); } catch (err) { void err; }
    load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch (err) { void err; }
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 rounded-full bg-white border border-[#E4DFCF] flex items-center justify-center hover:bg-[#F1EDDF]"
        data-testid="notifications-bell"
        aria-label="Notifications"
      >
        <Bell size={16} className="text-[#0F1E15]"/>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#C9A24B] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-medium" data-testid="notifications-unread">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-auto bg-white rounded-xl border border-[#E4DFCF] shadow-xl z-40" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4DFCF]">
            <div>
              <div className="font-serif text-lg">Notifications</div>
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{unread} unread</div>
            </div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#0F5E39] hover:underline" data-testid="mark-all-read-btn">Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#4A5A50]" data-testid="notifications-empty">
              You&apos;re all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-[#E4DFCF]">
              {items.map(n => {
                const inner = (
                  <div className={`px-4 py-3 hover:bg-[#F1EDDF]/60 cursor-pointer ${!n.read ? "bg-[#F6EED8]/30" : ""}`}>
                    <div className="flex items-start gap-3">
                      {!n.read && <div className="w-2 h-2 rounded-full bg-[#C9A24B] mt-1.5 shrink-0"/>}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#0F1E15] truncate">{n.title}</div>
                        {n.body && <div className="text-xs text-[#4A5A50] mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mt-1">
                          {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <li key={n.id} onClick={() => markRead(n.id)} data-testid={`notification-${n.id}`}>
                    {n.url ? <Link to={n.url} onClick={() => setOpen(false)}>{inner}</Link> : inner}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
